// components/Dashboard/AdvancedAnalytics.js
// Advanced Analytics Tab - Predictive Analytics, AI Insights, and Financial Health

import React, { useMemo, useState } from 'react';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  RadialBarChart,
  RadialBar,
  Legend,
  Cell
} from 'recharts';
import {
  TrendingUp,
  TrendingDown,
  Zap,
  AlertTriangle,
  Flame,
  Target,
  Brain,
  Lightbulb,
  RefreshCw,
  Calendar,
  DollarSign,
  Activity,
  ArrowUpRight,
  ArrowDownRight,
  CheckCircle,
  Sparkles
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  generateCashFlowForecast,
  calculateBurnRate,
  calculateRunway,
  detectAnomalies,
  detectRecurringPatterns,
  calculateFinancialHealthScore,
  generateSpendingHeatmap,
  calculateCategoryVelocity,
  calculateAverages
} from '../../utils/financialCalculations';
import {
  formatCurrency,
  getValueColor,
  getBudgetColor,
  getTooltipStyle,
  formatPercentage
} from '../../utils/chartHelpers';
import LockedFeature from '../Subscription/LockedFeature';

const AdvancedAnalytics = ({
  transactions = [],
  categories = [],
  currentBalance = 0,
  totalIncome = 0,
  totalExpenses = 0,
  monthlyBudget = 0,
  categoryData = [],
  themeMode = 'dark',
  isMobile = false,
  isTablet = false
}) => {
  const { t } = useTranslation();
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [scenarioValue, setScenarioValue] = useState(0);

  // Calculate all advanced metrics
  const advancedMetrics = useMemo(() => {
    // Cash flow forecast
    const forecast = generateCashFlowForecast(currentBalance, transactions, 3);
    
    // Burn rate
    const burnRate = calculateBurnRate(transactions);
    
    // Averages
    const averages = calculateAverages(transactions, 3);
    
    // Runway
    const runway = calculateRunway(currentBalance, burnRate.dailyBurnRate, averages.avgIncome);
    
    // Anomalies
    const anomalies = detectAnomalies(transactions).slice(0, 10); // Top 10
    
    // Recurring patterns
    const recurringPatterns = detectRecurringPatterns(transactions).slice(0, 5); // Top 5
    
    // Financial health score
    const healthScore = calculateFinancialHealthScore({
      totalIncome,
      totalExpenses,
      monthlyBudget,
      currentBalance,
      categoryData,
      transactions
    });
    
    // Spending heatmap
    const heatmap = generateSpendingHeatmap(transactions);
    
    // Category velocity
    const velocity = calculateCategoryVelocity(transactions, categories);
    
    return {
      forecast,
      burnRate,
      runway,
      anomalies,
      recurringPatterns,
      healthScore,
      heatmap,
      velocity,
      averages
    };
  }, [transactions, categories, currentBalance, totalIncome, totalExpenses, monthlyBudget, categoryData]);

  // Styles
  const cardStyle = {
    background: themeMode === 'light' ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.08)',
    backdropFilter: 'blur(20px)',
    border: themeMode === 'light' ? '1px solid rgba(255, 255, 255, 0.3)' : '1px solid rgba(255, 255, 255, 0.15)',
    borderRadius: isMobile ? '12px' : '16px',
    padding: isMobile ? '16px' : '24px',
    marginBottom: isMobile ? '12px' : '16px',
    color: themeMode === 'light' ? '#1f2937' : '#ffffff',
    boxShadow: themeMode === 'light' ? '0 8px 32px rgba(0, 0, 0, 0.1)' : '0 8px 32px rgba(0, 0, 0, 0.3)'
  };

  const titleStyle = {
    fontSize: isMobile ? '16px' : '20px',
    fontWeight: '700',
    marginBottom: isMobile ? '12px' : '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  };

  const subtitleStyle = {
    fontSize: isMobile ? '12px' : '14px',
    color: themeMode === 'light' ? 'rgba(31, 41, 55, 0.7)' : 'rgba(255, 255, 255, 0.7)',
    marginBottom: '12px'
  };

  const metricCardStyle = {
    background: themeMode === 'light' ? 'rgba(102, 126, 234, 0.1)' : 'rgba(102, 126, 234, 0.2)',
    borderRadius: isMobile ? '8px' : '12px',
    padding: isMobile ? '12px' : '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  };

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
    gap: isMobile ? '12px' : '16px',
    marginBottom: isMobile ? '16px' : '24px'
  };

  const tooltipStyle = getTooltipStyle(themeMode);

  return (
    <div style={{ padding: isMobile ? '8px' : '16px' }}>
      {/* Header */}
      <div style={cardStyle}>
        <h2 style={titleStyle}>
          <Brain size={isMobile ? 20 : 24} />
          {isMobile ? t('dashboard.advanced.title') : t('dashboard.advanced.titleFull')}
        </h2>
        <p style={subtitleStyle}>
          {t('dashboard.advanced.subtitle')}
        </p>
      </div>

      {/* Top Metrics Grid - Burn Rate & Runway */}
      <div style={gridStyle}>
        {/* Daily Burn Rate */}
        <div style={{...cardStyle, background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'}}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: isMobile ? '10px' : '12px', opacity: 0.9, marginBottom: '4px' }}>
                {t('dashboard.advanced.dailyBurnRate')}
              </div>
              <div style={{ fontSize: isMobile ? '24px' : '32px', fontWeight: '800' }}>
                {formatCurrency(advancedMetrics.burnRate.dailyBurnRate)}
              </div>
              <div style={{ fontSize: isMobile ? '10px' : '12px', opacity: 0.8, marginTop: '4px' }}>
                {advancedMetrics.burnRate.daysElapsed} days / {advancedMetrics.burnRate.daysElapsed + advancedMetrics.burnRate.daysRemaining} days
              </div>
            </div>
            <Flame size={isMobile ? 32 : 48} style={{ opacity: 0.3 }} />
          </div>
        </div>

        {/* Runway */}
        <div style={{
          ...cardStyle,
          background: advancedMetrics.runway.status === 'critical' 
            ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
            : advancedMetrics.runway.status === 'warning'
            ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
            : 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: isMobile ? '10px' : '12px', opacity: 0.9, marginBottom: '4px' }}>
                {t('dashboard.advanced.financialRunway')}
              </div>
              <div style={{ fontSize: isMobile ? '24px' : '32px', fontWeight: '800' }}>
                {advancedMetrics.runway.months === Infinity ? '∞' : `${advancedMetrics.runway.months}m ${advancedMetrics.runway.days}d`}
              </div>
              <div style={{ fontSize: isMobile ? '10px' : '12px', opacity: 0.8, marginTop: '4px' }}>
                {advancedMetrics.runway.status === 'positive' 
                  ? t('dashboard.advanced.positiveCashFlow')
                  : t('dashboard.advanced.daysUntilZero', { days: advancedMetrics.runway.totalDays })}
              </div>
            </div>
            <Target size={isMobile ? 32 : 48} style={{ opacity: 0.3 }} />
          </div>
        </div>

        {/* Monthly Projection */}
        <div style={{...cardStyle, background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'}}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: isMobile ? '10px' : '12px', opacity: 0.9, marginBottom: '4px' }}>
                {t('dashboard.advanced.monthProjection')}
              </div>
              <div style={{ fontSize: isMobile ? '24px' : '32px', fontWeight: '800' }}>
                {formatCurrency(advancedMetrics.burnRate.monthlyProjection)}
              </div>
              <div style={{ fontSize: isMobile ? '10px' : '12px', opacity: 0.8, marginTop: '4px' }}>
                {t('dashboard.advanced.atCurrentRate')}
              </div>
            </div>
            <Calendar size={isMobile ? 32 : 48} style={{ opacity: 0.3 }} />
          </div>
        </div>
      </div>

      {/* Cash Flow Forecast (gated by cashflowForecast → Premium) */}
      <LockedFeature
        feature="cashflowForecast"
        ctaPlan="premium"
        preview="overlay"
        promoText="Cashflow forecasting"
      >
      <div style={cardStyle}>
        <h3 style={titleStyle}>
          <TrendingUp size={isMobile ? 18 : 20} />
          {t('dashboard.advanced.cashFlowForecast')}
        </h3>
        <p style={subtitleStyle}>
          {t('dashboard.advanced.cashFlowSubtitle')}
        </p>
        <ResponsiveContainer width="100%" height={isMobile ? 250 : 350}>
          <AreaChart data={advancedMetrics.forecast}>
            <defs>
              <linearGradient id="balanceGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.1} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={themeMode === 'light' ? '#e5e7eb' : '#374151'} />
            <XAxis 
              dataKey="month" 
              tick={{ fontSize: isMobile ? 10 : 12, fill: themeMode === 'light' ? '#1f2937' : '#ffffff' }}
            />
            <YAxis 
              tickFormatter={(val) => formatCurrency(val, true)}
              tick={{ fontSize: isMobile ? 10 : 12, fill: themeMode === 'light' ? '#1f2937' : '#ffffff' }}
            />
            <Tooltip contentStyle={tooltipStyle} formatter={(val) => formatCurrency(val)} />
            <Area 
              type="monotone" 
              dataKey="upperBound" 
              stroke="transparent" 
              fill="#3b82f6" 
              fillOpacity={0.1} 
            />
            <Area 
              type="monotone" 
              dataKey="balance" 
              stroke="#3b82f6" 
              strokeWidth={3}
              fill="url(#balanceGradient)"
              strokeDasharray={(entry) => entry.type === 'projected' ? '5 5' : '0'}
            />
            <Area 
              type="monotone" 
              dataKey="lowerBound" 
              stroke="transparent" 
              fill="#3b82f6" 
              fillOpacity={0.1} 
            />
          </AreaChart>
        </ResponsiveContainer>
        <div style={{ 
          marginTop: '16px', 
          padding: '12px', 
          background: themeMode === 'light' ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.2)',
          borderRadius: '8px',
          fontSize: isMobile ? '12px' : '14px'
        }}>
          <strong>📊 Insight:</strong> Based on last 3 months average, you're projected to {advancedMetrics.averages.avgSavings > 0 ? 'gain' : 'lose'}{' '}
          <strong>{formatCurrency(Math.abs(advancedMetrics.averages.avgSavings * 3))}</strong> over the next 3 months.
        </div>
      </div>
      </LockedFeature>

      {/* Financial Health Score */}
      <div style={cardStyle}>
        <h3 style={titleStyle}>
          <CheckCircle size={isMobile ? 18 : 20} />
          {t('dashboard.advanced.healthScore')}
        </h3>
        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '24px', alignItems: 'center' }}>
          {/* Score Circle */}
          <div style={{ flex: 'none' }}>
            <ResponsiveContainer width={isMobile ? 200 : 250} height={isMobile ? 200 : 250}>
              <RadialBarChart 
                cx={isMobile ? 100 : 125}
                cy={isMobile ? 100 : 125}
                innerRadius="70%" 
                outerRadius="100%" 
                barSize={isMobile ? 20 : 30}
                data={[{ name: 'Score', value: advancedMetrics.healthScore.score, fill: getBudgetColor(100 - advancedMetrics.healthScore.score) }]}
                startAngle={90}
                endAngle={-270}
              >
                <RadialBar
                  minAngle={15}
                  background={{ fill: themeMode === 'light' ? '#e5e7eb' : '#374151' }}
                  clockWise
                  dataKey="value"
                  cornerRadius={10}
                />
                <text 
                  x="50%" 
                  y="45%" 
                  textAnchor="middle" 
                  dominantBaseline="middle"
                  style={{ fontSize: isMobile ? '32px' : '48px', fontWeight: '800', fill: themeMode === 'light' ? '#1f2937' : '#ffffff' }}
                >
                  {advancedMetrics.healthScore.score}
                </text>
                <text 
                  x="50%" 
                  y="60%" 
                  textAnchor="middle" 
                  dominantBaseline="middle"
                  style={{ fontSize: isMobile ? '16px' : '20px', fontWeight: '600', fill: getBudgetColor(100 - advancedMetrics.healthScore.score) }}
                >
                  Grade: {advancedMetrics.healthScore.grade}
                </text>
              </RadialBarChart>
            </ResponsiveContainer>
          </div>

          {/* Breakdown */}
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: isMobile ? '14px' : '16px', marginBottom: '12px', fontWeight: '600' }}>
              {t('dashboard.advanced.scoreBreakdown')}
            </div>
            {Object.entries(advancedMetrics.healthScore.breakdown).map(([key, data]) => (
              <div key={key} style={{ marginBottom: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', fontSize: isMobile ? '12px' : '14px' }}>
                  <span style={{ textTransform: 'capitalize' }}>{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                  <span style={{ fontWeight: '600' }}>{data.score}/{data.weight}</span>
                </div>
                <div style={{ 
                  width: '100%', 
                  height: '8px', 
                  background: themeMode === 'light' ? '#e5e7eb' : '#374151',
                  borderRadius: '4px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: `${(data.score / data.weight) * 100}%`,
                    height: '100%',
                    background: getBudgetColor(100 - (data.score / data.weight) * 100),
                    transition: 'width 0.5s ease'
                  }} />
                </div>
              </div>
            ))}
            
            {advancedMetrics.healthScore.recommendations.length > 0 && (
              <div style={{ 
                marginTop: '16px', 
                padding: '12px', 
                background: themeMode === 'light' ? 'rgba(245, 158, 11, 0.1)' : 'rgba(245, 158, 11, 0.2)',
                borderRadius: '8px'
              }}>
                <div style={{ fontSize: isMobile ? '12px' : '14px', fontWeight: '600', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Lightbulb size={16} />
                  {t('dashboard.advanced.recommendations')}
                </div>
                <ul style={{ margin: 0, paddingLeft: '20px', fontSize: isMobile ? '11px' : '13px' }}>
                  {advancedMetrics.healthScore.recommendations.map((rec, idx) => (
                    <li key={idx} style={{ marginBottom: '4px' }}>{rec}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Anomaly Detection */}
      {advancedMetrics.anomalies.length > 0 && (
        <div style={cardStyle}>
          <h3 style={titleStyle}>
            <AlertTriangle size={isMobile ? 18 : 20} />
            {t('dashboard.advanced.anomalyDetection')}
          </h3>
          <p style={subtitleStyle}>
            {t('dashboard.advanced.anomalySubtitle')}
          </p>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: isMobile ? '11px' : '13px', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${themeMode === 'light' ? '#e5e7eb' : '#374151'}` }}>
                  <th style={{ padding: '8px', textAlign: 'left' }}>{t('dashboard.advanced.date')}</th>
                  <th style={{ padding: '8px', textAlign: 'left' }}>{t('dashboard.advanced.description')}</th>
                  <th style={{ padding: '8px', textAlign: 'right' }}>{t('dashboard.advanced.amount')}</th>
                  <th style={{ padding: '8px', textAlign: 'right' }}>{t('dashboard.advanced.deviation')}</th>
                  <th style={{ padding: '8px', textAlign: 'right' }}>% Above Avg</th>
                </tr>
              </thead>
              <tbody>
                {advancedMetrics.anomalies.slice(0, 5).map((anomaly, idx) => (
                  <tr key={idx} style={{ borderBottom: `1px solid ${themeMode === 'light' ? '#f3f4f6' : '#1f2937'}` }}>
                    <td style={{ padding: '8px' }}>{new Date(anomaly.date).toLocaleDateString()}</td>
                    <td style={{ padding: '8px' }}>{anomaly.description}</td>
                    <td style={{ padding: '8px', textAlign: 'right', fontWeight: '600' }}>{formatCurrency(anomaly.amount)}</td>
                    <td style={{ padding: '8px', textAlign: 'right' }}>
                      <span style={{ 
                        padding: '2px 8px', 
                        borderRadius: '4px',
                        background: anomaly.severity === 'high' ? '#ef4444' : '#f59e0b',
                        color: '#ffffff',
                        fontSize: '10px',
                        fontWeight: '600'
                      }}>
                        {anomaly.deviation}σ
                      </span>
                    </td>
                    <td style={{ padding: '8px', textAlign: 'right', color: '#ef4444', fontWeight: '600' }}>
                      +{anomaly.percentageAboveAverage}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recurring Patterns */}
      {advancedMetrics.recurringPatterns.length > 0 && (
        <div style={cardStyle}>
          <h3 style={titleStyle}>
            <RefreshCw size={isMobile ? 18 : 20} />
            {t('dashboard.advanced.recurringPatterns')}
          </h3>
          <p style={subtitleStyle}>
            Automatically detected recurring expenses (subscriptions, bills, etc.)
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {advancedMetrics.recurringPatterns.map((pattern, idx) => (
              <div key={idx} style={{
                padding: '12px',
                background: themeMode === 'light' ? 'rgba(102, 126, 234, 0.1)' : 'rgba(102, 126, 234, 0.2)',
                borderRadius: '8px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '8px'
              }}>
                <div style={{ flex: 1, minWidth: '150px' }}>
                  <div style={{ fontWeight: '600', fontSize: isMobile ? '13px' : '15px' }}>
                    {pattern.description}
                  </div>
                  <div style={{ fontSize: isMobile ? '11px' : '12px', opacity: 0.8, marginTop: '4px' }}>
                    {pattern.frequency} • {pattern.occurrences} occurrences • {pattern.confidence} confidence
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: isMobile ? '16px' : '18px', fontWeight: '700' }}>
                    {formatCurrency(pattern.amount)}
                  </div>
                  <div style={{ fontSize: isMobile ? '10px' : '11px', opacity: 0.8 }}>
                    Next: {new Date(pattern.nextExpectedDate).toLocaleDateString()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Day of Week Spending Heatmap */}
      <div style={cardStyle}>
        <h3 style={titleStyle}>
          <Activity size={isMobile ? 18 : 20} />
          {t('dashboard.advanced.spendingByDay')}
        </h3>
        <ResponsiveContainer width="100%" height={isMobile ? 200 : 300}>
          <BarChart data={advancedMetrics.heatmap}>
            <CartesianGrid strokeDasharray="3 3" stroke={themeMode === 'light' ? '#e5e7eb' : '#374151'} />
            <XAxis 
              dataKey="day" 
              tick={{ fontSize: isMobile ? 10 : 12, fill: themeMode === 'light' ? '#1f2937' : '#ffffff' }}
            />
            <YAxis 
              tickFormatter={(val) => formatCurrency(val, true)}
              tick={{ fontSize: isMobile ? 10 : 12, fill: themeMode === 'light' ? '#1f2937' : '#ffffff' }}
            />
            <Tooltip contentStyle={tooltipStyle} formatter={(val) => formatCurrency(val)} />
            <Bar dataKey="average" fill="#3b82f6" radius={[8, 8, 0, 0]}>
              {advancedMetrics.heatmap.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={`rgba(59, 130, 246, ${0.3 + (entry.average / Math.max(...advancedMetrics.heatmap.map(d => d.average))) * 0.7})`} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        <div style={{ 
          marginTop: '16px', 
          padding: '12px', 
          background: themeMode === 'light' ? 'rgba(102, 126, 234, 0.1)' : 'rgba(102, 126, 234, 0.2)',
          borderRadius: '8px',
          fontSize: isMobile ? '12px' : '14px'
        }}>
          <strong>📅 Pattern:</strong> You tend to spend most on{' '}
          <strong>{advancedMetrics.heatmap.reduce((max, day) => day.average > max.average ? day : max).day}</strong>
        </div>
      </div>

      {/* Category Velocity */}
      {advancedMetrics.velocity.length > 0 && (
        <div style={cardStyle}>
          <h3 style={titleStyle}>
            <Zap size={isMobile ? 18 : 20} />
            Category Spending Velocity
          </h3>
          <p style={subtitleStyle}>
            Daily spending rate per category - early warning for budget overruns
          </p>
          {advancedMetrics.velocity.slice(0, 5).map((cat, idx) => (
            <div key={idx} style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: isMobile ? '12px' : '14px' }}>
                <span style={{ fontWeight: '600' }}>{cat.name}</span>
                <span>{formatCurrency(cat.dailyRate)}/day</span>
              </div>
              {cat.status === 'warning' && (
                <div style={{
                  padding: '8px',
                  background: 'rgba(245, 158, 11, 0.2)',
                  borderRadius: '6px',
                  fontSize: isMobile ? '11px' : '12px',
                  color: '#f59e0b',
                  marginBottom: '6px'
                }}>
                  ⚠️ At this rate, you'll exceed budget in {cat.daysUntilOverBudget} days
                </div>
              )}
              <div style={{ 
                width: '100%', 
                height: '10px', 
                background: themeMode === 'light' ? '#e5e7eb' : '#374151',
                borderRadius: '5px',
                overflow: 'hidden'
              }}>
                <div style={{
                  width: `${Math.min((cat.spent / cat.budget) * 100, 100)}%`,
                  height: '100%',
                  background: getBudgetColor((cat.spent / cat.budget) * 100),
                  transition: 'width 0.5s ease'
                }} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* What-If Scenarios (Simple Interactive Tool) */}
      <div style={cardStyle}>
        <h3 style={titleStyle}>
          <Target size={isMobile ? 18 : 20} />
          What-If Scenario Planner
        </h3>
        <p style={subtitleStyle}>
          See the impact of spending changes on your financial goals
        </p>
        <div style={{ marginBottom: '16px' }}>
          <label style={{ fontSize: isMobile ? '12px' : '14px', marginBottom: '8px', display: 'block' }}>
            If I reduce spending by:
          </label>
          <input 
            type="range" 
            min="0" 
            max="50" 
            step="5"
            value={scenarioValue}
            onChange={(e) => setScenarioValue(Number(e.target.value))}
            style={{ 
              width: '100%', 
              height: '8px',
              borderRadius: '4px',
              outline: 'none',
              cursor: 'pointer'
            }}
          />
          <div style={{ textAlign: 'center', fontSize: isMobile ? '18px' : '24px', fontWeight: '700', marginTop: '8px', color: '#10b981' }}>
            {scenarioValue}%
          </div>
        </div>
        <div style={gridStyle}>
          <div style={metricCardStyle}>
            <div style={{ fontSize: isMobile ? '11px' : '12px', opacity: 0.8 }}>New Monthly Expenses</div>
            <div style={{ fontSize: isMobile ? '18px' : '24px', fontWeight: '700' }}>
              {formatCurrency(totalExpenses * (1 - scenarioValue / 100))}
            </div>
          </div>
          <div style={metricCardStyle}>
            <div style={{ fontSize: isMobile ? '11px' : '12px', opacity: 0.8 }}>Monthly Savings Increase</div>
            <div style={{ fontSize: isMobile ? '18px' : '24px', fontWeight: '700', color: '#10b981' }}>
              +{formatCurrency(totalExpenses * (scenarioValue / 100))}
            </div>
          </div>
          <div style={metricCardStyle}>
            <div style={{ fontSize: isMobile ? '11px' : '12px', opacity: 0.8 }}>Annual Impact</div>
            <div style={{ fontSize: isMobile ? '18px' : '24px', fontWeight: '700', color: '#3b82f6' }}>
              {formatCurrency(totalExpenses * (scenarioValue / 100) * 12)}
            </div>
          </div>
        </div>
      </div>

      {/* Footer Insight */}
      <div style={{
        ...cardStyle,
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: '#ffffff',
        textAlign: 'center'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '8px' }}>
          <Sparkles size={20} />
          <span style={{ fontSize: isMobile ? '14px' : '16px', fontWeight: '600' }}>AI-Powered Analytics</span>
        </div>
        <p style={{ fontSize: isMobile ? '12px' : '13px', opacity: 0.9, margin: 0 }}>
          All predictions and insights are generated using advanced statistical analysis and machine learning algorithms based on your transaction history.
        </p>
      </div>
    </div>
  );
};

export default AdvancedAnalytics;
